import React, { useMemo, useState } from 'react';
import { Row, Col, Spin } from 'antd';
import { Formik, FormikHelpers } from 'formik';
import { Form, FormItem, Input, Select, SubmitButton } from 'formik-antd';
import { TIMEZONE } from 'constants/options';
import { PublicRegisterValidation } from 'schemas/publicRegister';
import { usePublicRegister } from 'hooks/publicHooks';
import { ImLocation } from 'react-icons/im';
import { BsFillPhoneFill, BsFillClockFill, BsFileEarmarkBinaryFill } from 'react-icons/bs';
import { svgIcons } from 'resources/icons';
import { IoIosMail } from 'react-icons/io';
import Logo from 'resources/images/Group_34.png';
// Reuse login page layout and background styles
import '../auth/pages/Login.less';

const { Option } = Select;

const PublicRegister: React.FC = () => {
  const registerMutation = usePublicRegister();
  const [submitted, setSubmitted] = useState<boolean>(false);

  const initialValues = useMemo(
    () => ({
      companyName: '',
      usdot: '',
      email: '',
      phone: '',
      companyAddress: '',
      homeAddress: '',
      timeZone: '', // Home Terminal Timezone (tzCode)
    }),
    [],
  );

  const onSubmit = async (values: typeof initialValues, helpers: FormikHelpers<typeof initialValues>) => {
    helpers.setSubmitting(true);
    try {
      const numericPhone = values.phone.replace(/\D/g, '');
      // Auto-generate a strong password for the initial admin
      const autoPassword = 'ATestAdmin1!';

      const payload = {
        company: {
          name: values.companyName,
          address: values.companyAddress,
          timeZone: values.timeZone,
          email: values.email.trim(),
          phoneNumber: numericPhone,
          usdot: values.usdot,
        },
        user: {
          email: values.email.trim(),
          password: autoPassword,
          userName: values.email.trim(),
          firstName: 'Admin',
          lastName: 'User',
          timeZone: values.timeZone,
          gender: 'Male' as const,
          phoneNumber: numericPhone,
        },
        office: {
          name: `Head Office - ${values.companyName}`,
          address: values.homeAddress,
          phoneNumber: numericPhone,
          timeZone: values.timeZone,
          country: 'US',
          state: 'CA',
          city: 'Default San Diego',
        },
      };

      await registerMutation.mutateAsync(payload as any);
      // Do not navigate away; show a thank-you panel and hide the form
      setSubmitted(true);
    } catch (error: any) {
      // Surface errors inline if needed; keep user on the page
      console.log('Registration error:', JSON.stringify(error));
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <Spin spinning={registerMutation.isLoading}>
      <div className="login-form-wrapper">
        <Row className="form-container">
          <Col offset={12} span={12} className="blur_bg">
            <div className="RegisterForm">
              <Row>
                <Col span={24}>
                  <div className="comapny_logo" style={{ justifyContent: 'center' }}>
                    <img className="img-fluid logo-img pb-2" src={Logo} alt="logo" />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <div className="welcome-text" style={{ textAlign: 'center' }}>
                    <p>Request access</p>
                  </div>
                </Col>
              </Row>

              {!submitted ? (
                <Formik initialValues={initialValues} validationSchema={PublicRegisterValidation} onSubmit={onSubmit}>
                  {() => (
                    <Form size="large" layout="vertical" initialValues={initialValues} className="loginForm">
                      <Row gutter={24}>
                        <Col span={12}>
                          <FormItem label="Company Name" name="companyName" required>
                            <Input
                              name="companyName"
                              maxLength={50}
                              placeholder="Company name"
                              prefix={<svgIcons.CompanyIcon width={18} height={18} />}
                            />
                          </FormItem>
                        </Col>
                        <Col span={12}>
                          <FormItem label="USDOT" name="usdot" required>
                            <Input
                              name="usdot"
                              maxLength={12}
                              placeholder="USDOT number"
                              prefix={<BsFileEarmarkBinaryFill size={18} />}
                            />
                          </FormItem>
                        </Col>
                      </Row>

                      <Row gutter={24}>
                        <Col span={12}>
                          <FormItem label="Company Address" name="companyAddress" required>
                            <Input
                              name="companyAddress"
                              prefix={<ImLocation size={20} />}
                              maxLength={100}
                              placeholder="Street address"
                            />
                          </FormItem>
                        </Col>
                        <Col span={12}>
                          <FormItem label="Email" name="email" required>
                            <Input name="email" prefix={<IoIosMail size={20} />} placeholder="Email" />
                          </FormItem>
                        </Col>
                      </Row>

                      <Row gutter={24}>
                        <Col span={12}>
                          <FormItem label="Phone" name="phone" required>
                            <Input
                              name="phone"
                              prefix={<BsFillPhoneFill size={20} />}
                              maxLength={15}
                              placeholder="Phone"
                            />
                          </FormItem>
                        </Col>
                        <Col span={12}>
                          <FormItem label="Home Terminal Timezone" name="timeZone" required>
                            <BsFillClockFill size={20} className="selectIcons" />
                            <div className="iconBorder" />
                            <Select className="tzSelect" name="timeZone" showSearch placeholder="Select timezone">
                              {TIMEZONE.map(tz => (
                                <Option key={tz.tzCode} value={tz.tzCode}>
                                  {tz.label}
                                </Option>
                              ))}
                            </Select>
                          </FormItem>
                        </Col>
                      </Row>

                      <Row gutter={24}>
                        <Col span={24}>
                          <FormItem label="Home Terminal Address" name="homeAddress" required>
                            <Input
                              name="homeAddress"
                              prefix={<ImLocation size={20} />}
                              maxLength={100}
                              placeholder="Street address"
                            />
                          </FormItem>
                        </Col>
                      </Row>

                      <Row gutter={24}>
                        <Col span={24} className="align-submit-buttons submit-section">
                          <SubmitButton htmlType="submit" className="login-btn">
                            Request Access
                          </SubmitButton>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              ) : (
                <div style={{ padding: '0 30px', textAlign: 'center' }}>
                  <h3>Your access request has been received!</h3>
                  <p style={{ color: '#586973', marginTop: '16px' }}>
                    Thank you for your interest in the LogELD Platform. Our support team will review your request and
                    get in touch with your account details shortly.
                  </p>
                  <p style={{ color: '#586973' }}>We’re excited to have you on board!</p>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </Spin>
  );
};

export default PublicRegister;
